import { useEffect, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Mail,
  UserPlus,
  UserMinus,
  Users,
  FileText,
  Settings,
  Shield,
  Clock,
  TrendingUp,
  Filter,
  Calendar,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ActivityItem {
  id: string;
  action: string;
  user_name: string;
  created_at: string;
  resource_type?: string;
  resource_id?: string;
  details: Record<string, any>;
}

interface ActivitySummary {
  total_activities: number;
  activity_by_type: Record<string, number>;
  recent_activities: ActivityItem[];
  active_users: number;
}

const actionConfig: Record<string, { icon: any; color: string; label: string }> = {
  team_created: { icon: Users, color: "text-green-600", label: "Team Created" },
  team_updated: { icon: Settings, color: "text-blue-600", label: "Team Updated" },
  member_invited: { icon: UserPlus, color: "text-purple-600", label: "Member Invited" },
  member_joined: { icon: UserPlus, color: "text-green-600", label: "Member Joined" },
  member_removed: { icon: UserMinus, color: "text-red-600", label: "Member Removed" },
  member_updated: { icon: Shield, color: "text-yellow-600", label: "Member Updated" },
  email_shared: { icon: Mail, color: "text-blue-600", label: "Email Shared" },
  email_unshared: { icon: Mail, color: "text-gray-600", label: "Email Unshared" },
  email_fetched: { icon: Mail, color: "text-indigo-600", label: "Emails Fetched" },
  player_created: { icon: UserPlus, color: "text-green-600", label: "Player Added" },
  player_updated: { icon: FileText, color: "text-blue-600", label: "Player Updated" },
  player_deleted: { icon: UserMinus, color: "text-red-600", label: "Player Removed" },
  shortlist_created: { icon: FileText, color: "text-purple-600", label: "Shortlist Created" },
  shortlist_updated: { icon: FileText, color: "text-blue-600", label: "Shortlist Updated" },
  shortlist_shared: { icon: Users, color: "text-indigo-600", label: "Shortlist Shared" },
};

export function TeamActivityFeed({ teamId }: { teamId: string }) {
  const [timeRange, setTimeRange] = useState(7);
  const [filterType, setFilterType] = useState<string>("all");
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch activity summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["team-activity-summary", teamId, timeRange],
    queryFn: async () => {
      const response = await api.get(`/api/teams/${teamId}/activity/summary?days=${timeRange}`);
      return response.data as ActivitySummary;
    },
    refetchInterval: autoRefresh ? 30000 : false, // Refresh every 30 seconds if enabled
  });

  // Fetch detailed activities
  const { data: activities, isLoading: activitiesLoading } = useQuery({
    queryKey: ["team-activity", teamId],
    queryFn: async () => {
      const response = await api.get(`/api/teams/${teamId}/activity?limit=100`);
      return response.data as ActivityItem[];
    },
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const getActionConfig = (action: string) => {
    return actionConfig[action] || {
      icon: Activity,
      color: "text-gray-600",
      label: action.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
    };
  };

  const getActionDescription = (activity: ActivityItem) => {
    const { action, details } = activity;
    
    switch (action) {
      case "member_invited":
        return `invited ${details.invited_email} to join the team`;
      case "member_joined":
        return `joined the team`;
      case "member_removed":
        return details.removed_by_self 
          ? "left the team" 
          : `removed ${details.removed_user_name || "a member"} from the team`;
      case "member_updated":
        if (details.updates?.email_sharing_enabled !== undefined) {
          return details.updates.email_sharing_enabled
            ? "enabled email sharing"
            : "disabled email sharing";
        }
        return "updated member settings";
      case "email_fetched":
        return `fetched ${details.email_count || 0} emails from ${details.email_address}`;
      case "player_created":
        return `added player ${details.player_name}`;
      case "player_updated":
        return `updated player information`;
      case "shortlist_created":
        return `created shortlist "${details.shortlist_name}"`;
      case "shortlist_updated":
        return `updated shortlist "${details.shortlist_name}"`;
      default:
        return action.replace(/_/g, " ");
    }
  };

  const filteredActivities = activities?.filter(activity => {
    if (filterType === "all") return true;
    return activity.action.includes(filterType);
  }) || [];

  const activityTypeOptions = [
    { value: "all", label: "All Activities" },
    { value: "member", label: "Team Members" },
    { value: "email", label: "Email Activities" },
    { value: "player", label: "Player Activities" },
    { value: "shortlist", label: "Shortlist Activities" },
  ];

  if (summaryLoading || activitiesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Activities</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.total_activities || 0}</div>
            <p className="text-xs text-muted-foreground">
              in the last {timeRange} days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.active_users || 0}</div>
            <p className="text-xs text-muted-foreground">
              members with activity
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Most Common</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.activity_by_type && Object.keys(summary.activity_by_type).length > 0
                ? Object.entries(summary.activity_by_type)
                    .sort(([, a], [, b]) => b - a)[0][0]
                    .replace(/_/g, " ")
                : "No activity"}
            </div>
            <p className="text-xs text-muted-foreground">
              activity type
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Daily</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.total_activities 
                ? Math.round(summary.total_activities / timeRange) 
                : 0}
            </div>
            <p className="text-xs text-muted-foreground">
              activities per day
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Activity Feed</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activityTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Clock className="h-4 w-4 mr-2" />
                    {timeRange}d
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40">
                  <div className="space-y-2">
                    {[1, 7, 14, 30].map(days => (
                      <Button
                        key={days}
                        variant={timeRange === days ? "default" : "ghost"}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setTimeRange(days)}
                      >
                        Last {days} day{days > 1 ? "s" : ""}
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? "Auto" : "Manual"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {filteredActivities.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No activities found for the selected filter
                </p>
              ) : (
                filteredActivities.map(activity => {
                  const config = getActionConfig(activity.action);
                  const Icon = config.icon;
                  
                  return (
                    <div
                      key={activity.id}
                      className="flex items-start space-x-3 pb-3 border-b last:border-0 last:pb-0"
                    >
                      <div className={cn(
                        "p-2 rounded-full bg-muted",
                        config.color
                      )}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm">
                          <span className="font-medium">{activity.user_name}</span>{" "}
                          {getActionDescription(activity)}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {config.label}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(activity.created_at), { 
                              addSuffix: true 
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}